import { useEffect, useState } from "react";
import { Users, Calendar, MessageSquare, Mail, RefreshCw, PhoneCall } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30",
  confirmed: "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

const CALL_COLORS = {
  queued: "bg-[#64748B]/15 text-[#94A3B8] border-[#64748B]/30",
  dispatched: "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/30",
  ringing: "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/30",
  in_progress: "bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  qualified: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  no_answer: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  voicemail: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  declined: "bg-red-500/15 text-red-300 border-red-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};

const TABS = [
  { id: "overview", label: "Overview", icon: Users },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "leads", label: "Leads", icon: Mail },
  { id: "voice", label: "Voice Calls", icon: PhoneCall },
  { id: "chats", label: "AI Chats", icon: MessageSquare },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({ leads: 0, bookings: 0, users: 0, chat_sessions: 0, voice_calls: 0 });
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadAll = async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, b, c, ses, vc] = await Promise.all([
        api.get("/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
        api.get("/admin/chat-sessions"),
        api.get("/admin/voice-calls").catch(() => ({ data: { calls: [] } })),
      ]);
      setStats(s.data);
      setBookings(b.data.bookings);
      setContacts(c.data.contacts);
      setSessions(ses.data.sessions);
      setCalls(vc.data.calls || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/bookings/${id}`, { status });
    setBookings((bs) => bs.map((x) => (x.id === id ? { ...x, status } : x)));
  };

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-6 md:px-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Admin control room</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-bold text-white">ZeroLabs Digitals Operations</h1>
        </div>
        <button data-testid="admin-refresh" onClick={loadAll} className="btn-secondary !py-2 !px-4 text-sm">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {err && <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{err}</div>}

      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat label="Users" value={stats.users} icon={<Users size={16}/>}/>
        <Stat label="Bookings" value={stats.bookings} icon={<Calendar size={16}/>} accent/>
        <Stat label="Leads" value={stats.leads} icon={<Mail size={16}/>}/>
        <Stat label="Voice calls" value={stats.voice_calls || 0} icon={<PhoneCall size={16}/>}/>
        <Stat label="Chat sessions" value={stats.chat_sessions} icon={<MessageSquare size={16}/>}/>
      </div>

      <div className="mt-10 flex gap-2 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                active ? "border-[#22D3EE] text-white" : "border-transparent text-[#94A3B8] hover:text-white"
              }`}
            >
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        {loading && <div className="text-[#94A3B8] py-6">Loading…</div>}

        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-5">
            <div className="card p-6">
              <h3 className="text-white font-semibold">Recent bookings</h3>
              <ul className="mt-3 divide-y divide-white/5">
                {bookings.slice(0, 5).map((b) => (
                  <li key={b.id} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="text-white">{b.name} • {b.service}</div>
                      <div className="text-[#64748B] text-xs">{b.preferred_date} {b.preferred_time}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-md border text-xs uppercase tracking-[0.15em] ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                  </li>
                ))}
                {bookings.length === 0 && <li className="py-3 text-[#94A3B8] text-sm">No bookings yet.</li>}
              </ul>
            </div>
            <div className="card p-6">
              <h3 className="text-white font-semibold">Recent leads</h3>
              <ul className="mt-3 divide-y divide-white/5">
                {contacts.slice(0, 5).map((c) => (
                  <li key={c.id} className="py-3 text-sm">
                    <div className="text-white">{c.name} <span className="text-[#64748B]">• {c.email}</span></div>
                    <div className="text-[#94A3B8] text-xs mt-1 line-clamp-2">{c.message}</div>
                  </li>
                ))}
                {contacts.length === 0 && <li className="py-3 text-[#94A3B8] text-sm">No leads yet.</li>}
              </ul>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[#64748B] uppercase text-[10px] tracking-[0.2em]">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Service</th>
                  <th className="text-left px-5 py-3">Date/Time</th>
                  <th className="text-left px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`admin-booking-${b.id}`}>
                    <td className="px-5 py-3 text-white">{b.name}</td>
                    <td className="px-5 py-3 text-[#94A3B8]">{b.email}</td>
                    <td className="px-5 py-3 text-[#94A3B8]">{b.service}</td>
                    <td className="px-5 py-3 text-[#94A3B8]">{b.preferred_date} {b.preferred_time}</td>
                    <td className="px-5 py-3">
                      <select
                        data-testid={`admin-booking-status-${b.id}`}
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        className={`input-field !py-1.5 !px-2 text-xs uppercase tracking-[0.15em] !rounded-md ${STATUS_COLORS[b.status]}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {bookings.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-[#94A3B8]">No bookings yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "leads" && (
          <div className="grid md:grid-cols-2 gap-4">
            {contacts.map((c) => (
              <div key={c.id} className="card p-5" data-testid={`admin-lead-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-white font-semibold">{c.name}</div>
                  <span className="text-xs text-[#64748B] font-mono">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-[#94A3B8] text-xs mt-1">{c.email} • {c.company || "—"}</div>
                {c.service && <div className="mt-2 text-xs"><span className="text-[#22D3EE]">{c.service}</span></div>}
                <p className="text-[#E2E8F0] text-sm mt-3 leading-relaxed">{c.message}</p>
              </div>
            ))}
            {contacts.length === 0 && <div className="text-[#94A3B8]">No leads yet.</div>}
          </div>
        )}

        {tab === "chats" && (
          <ChatSessions sessions={sessions}/>
        )}

        {tab === "voice" && (
          <VoiceCalls calls={calls}/>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon, accent }) {
  return (
    <div className={`card p-5 ${accent ? "glow-border" : ""}`}>
      <div className="flex items-center gap-2 text-[#64748B] text-xs uppercase tracking-[0.2em]">{icon} {label}</div>
      <div className="mt-2 text-3xl font-extrabold text-white font-mono">{value}</div>
    </div>
  );
}

function ChatSessions({ sessions }) {
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);

  const open = async (s) => {
    setActive(s);
    const { data } = await api.get(`/chat/${s.id}/messages`);
    setMessages(data.messages);
  };

  return (
    <div className="grid md:grid-cols-3 gap-4">
      <div className="card p-2 max-h-[60vh] overflow-y-auto">
        {sessions.map((s) => (
          <button
            key={s.id}
            data-testid={`admin-chat-${s.id}`}
            onClick={() => open(s)}
            className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${active?.id === s.id ? "bg-white/5" : "hover:bg-white/[0.03]"}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-white truncate max-w-[180px]">{s.first_message || "(no messages)"}</div>
              <span className="text-[10px] text-[#64748B] font-mono">{s.message_count}</span>
            </div>
            <div className="text-[10px] text-[#64748B] font-mono mt-1">{new Date(s.last_message_at).toLocaleString()}</div>
          </button>
        ))}
        {sessions.length === 0 && <div className="p-4 text-sm text-[#94A3B8]">No chat sessions yet.</div>}
      </div>
      <div className="md:col-span-2 card p-4 max-h-[60vh] overflow-y-auto">
        {!active && <div className="text-[#94A3B8] text-sm p-4">Select a session to view the transcript.</div>}
        {active && (
          <div className="space-y-3">
            {messages.map((m) => (
              <div key={m.id} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === "user" ? "ml-auto bg-[#3B82F6] text-[#08060F]" : "bg-white/5 border border-white/10 text-white"}`}>
                {m.content}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function VoiceCalls({ calls }) {
  const [active, setActive] = useState(null);
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="admin-voice-calls">
      <div className="card p-2 max-h-[65vh] overflow-y-auto lg:col-span-1">
        {calls.map((c) => (
          <button
            key={c.id}
            data-testid={`admin-voice-call-${c.id}`}
            onClick={() => setActive(c)}
            className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${active?.id === c.id ? "bg-white/5" : "hover:bg-white/[0.03]"}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-white truncate">{c.lead_name || "Unknown"}</div>
              <span className={`px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-[0.15em] shrink-0 ${CALL_COLORS[c.status] || CALL_COLORS.queued}`}>{(c.status || "queued").replace("_", " ")}</span>
            </div>
            <div className="text-[11px] text-[#64748B] font-mono mt-1">{c.lead_phone || "—"} • {c.duration_sec ? `${c.duration_sec}s` : "—"}</div>
            <div className="text-[10px] text-[#64748B] font-mono mt-1">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</div>
          </button>
        ))}
        {calls.length === 0 && (
          <div className="p-4 text-sm text-[#94A3B8]">
            No voice calls yet. Once a visitor submits the contact form, Zero will call them and the transcript will appear here.
          </div>
        )}
      </div>
      <div className="lg:col-span-2 card p-5 max-h-[65vh] overflow-y-auto">
        {!active && <div className="text-[#94A3B8] text-sm p-4">Select a call to view summary & transcript.</div>}
        {active && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-white text-lg font-semibold">{active.lead_name}</div>
                <span className={`px-2 py-0.5 rounded-md border text-[10px] uppercase tracking-[0.15em] ${CALL_COLORS[active.status] || CALL_COLORS.queued}`}>{(active.status || "queued").replace("_", " ")}</span>
              </div>
              <div className="text-xs text-[#94A3B8] mt-1">
                {active.lead_phone || "—"} • {active.lead_email || "—"} • {active.duration_sec ? `${active.duration_sec}s` : "—"}
                {active.language ? ` • ${active.language}` : ""}
              </div>
            </div>

            {active.lead_message && (
              <Block title="Original message">{active.lead_message}</Block>
            )}
            {active.intent && <Block title="Detected intent" accent="orange">{active.intent}</Block>}
            {active.summary && <Block title="Call summary" accent="purple">{active.summary}</Block>}
            {active.next_steps && <Block title="Next steps">{active.next_steps}</Block>}

            {active.recording_url && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-2">Recording</div>
                <audio data-testid="voice-recording" controls src={active.recording_url} className="w-full"/>
              </div>
            )}

            {active.transcript && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-2">Transcript</div>
                <pre className="text-[13px] text-[#E2E8F0] whitespace-pre-wrap leading-relaxed bg-white/[0.02] border border-white/5 rounded-lg p-3 font-sans">{active.transcript}</pre>
              </div>
            )}

            {active.error && (
              <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <span className="font-semibold">Error:</span> {active.error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Block({ title, children, accent }) {
  const accentCls = accent === "orange" ? "text-[#3B82F6]" : accent === "purple" ? "text-[#22D3EE]" : "text-[#64748B]";
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1.5 ${accentCls}`}>{title}</div>
      <div className="text-sm text-[#E2E8F0] leading-relaxed whitespace-pre-wrap">{children}</div>
    </div>
  );
}
